import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getShowcaseImages } from '../../services/galleryService';
import Icon from '../../components/AppIcon';
import AppImage from '../../components/AppImage';
import Header from '../../components/ui/Header';

const ShowcaseGallery = () => {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState({});

  // Fetch showcase images on mount
  useEffect(() => {
    const fetchImages = async () => {
      try {
        setLoading(true);
        const showcaseImages = await getShowcaseImages();
        setImages(showcaseImages);
      } catch (error) {
        console.error('Error loading showcase images:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, []);

  // Auto-advance carousel every 5 seconds
  useEffect(() => {
    if (!isPlaying || images?.length === 0) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % images?.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isPlaying, images?.length]);

  // Carousel navigation
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % images?.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + images?.length) % images?.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Lightbox controls
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    setIsPlaying(false); // Pause carousel when lightbox opens
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setIsPlaying(true); // Resume carousel when lightbox closes
  };

  const nextLightboxImage = () => {
    setLightboxIndex((prev) => (prev + 1) % images?.length);
  };

  const prevLightboxImage = () => {
    setLightboxIndex((prev) => (prev - 1 + images?.length) % images?.length);
  };

  // Handle image load
  const handleImageLoad = (index) => {
    setImageLoaded(prev => ({ ...prev, [index]: true }));
  };

  const handleSignUp = () => {
    navigate('/register');
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Background Image with Overlay - Same as Login Page */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url("/assets/images/c22a2fdf-5d74-4a81-9934-01f417fff487_121132386-1766899692964.png")'
        }}
      >
        <div className="absolute inset-0 bg-black/25 backdrop-blur-[2px]" />
      </div>
      {/* Header */}
      <Header isAuthenticated={false} onLogout={() => {}} />
      {/* Main Content */}
      <div className="relative pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12 animate-fade-in">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 drop-shadow-lg">
              Professional Planner Customization
            </h1>
            <p className="font-body text-lg sm:text-xl text-white/90 max-w-3xl mx-auto drop-shadow">
              Discover the power of personalized planning designed for professionals
            </p>
          </div>

          {/* Carousel Section */}
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-6 sm:p-8 mb-12 animate-fade-in">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-6 text-center drop-shadow-lg">
              Gallery Showcase
            </h2>

            {loading ? (
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                  <p className="font-body text-white/90">Loading gallery...</p>
                </div>
              </div>
            ) : images?.length === 0 ? (
              <div className="flex items-center justify-center min-h-[400px]">
                <p className="font-body text-white/90">No showcase images available</p>
              </div>
            ) : (
              <div 
                className="relative"
                onMouseEnter={() => setIsPlaying(false)}
                onMouseLeave={() => setIsPlaying(true)}
              >
                {/* Carousel Container */}
                <div className="relative bg-white/5 rounded-xl overflow-hidden mb-6 min-h-[400px] sm:min-h-[500px] lg:min-h-[600px]">
                  {images?.map((image, index) => (
                    <div
                      key={image?.id}
                      className={`absolute inset-0 transition-opacity duration-700 cursor-pointer ${
                        index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
                      }`}
                      onClick={() => openLightbox(index)}
                    >
                      {/* Image Loading Skeleton */}
                      {!imageLoaded?.[index] && (
                        <div className="absolute inset-0 bg-white/10 animate-pulse flex items-center justify-center">
                          <Icon name="Image" size={48} className="text-white/30" />
                        </div>
                      )}
                      
                      {/* Actual Image */}
                      <AppImage
                        src={image?.url}
                        alt={image?.caption || 'Planner showcase image'}
                        className={`w-full h-full object-contain transition-opacity duration-300 ${
                          imageLoaded?.[index] ? 'opacity-100' : 'opacity-0'
                        }`}
                        onLoad={() => handleImageLoad(index)}
                      />
                    </div>
                  ))}

                  {/* Navigation Arrows */}
                  <button
                    onClick={prevSlide}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 rounded-full p-3 transition-all duration-300 hover:scale-110 z-20"
                    aria-label="Previous slide"
                  >
                    <Icon name="ChevronLeft" size={24} className="text-white" />
                  </button>

                  <button
                    onClick={nextSlide}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 rounded-full p-3 transition-all duration-300 hover:scale-110 z-20"
                    aria-label="Next slide"
                  >
                    <Icon name="ChevronRight" size={24} className="text-white" />
                  </button>
                </div>

                {/* Caption Area */}
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-4 text-center">
                  <p className="font-body text-white/90 text-lg drop-shadow">
                    {images?.[currentSlide]?.caption}
                  </p>
                </div>

                {/* Slide Indicators */}
                <div className="flex justify-center items-center space-x-2 mb-3">
                  {images?.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToSlide(index)}
                      className={`transition-all duration-300 rounded-full ${
                        index === currentSlide
                          ? 'w-8 h-3 bg-white' : 'w-3 h-3 bg-white/40 hover:bg-white/60'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>

                {/* Slide Counter */}
                <div className="text-center">
                  <span className="font-body text-sm text-white/70">
                    {currentSlide + 1} / {images?.length}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Features Section */}
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-6 sm:p-8 mb-12 animate-fade-in">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-8 text-center drop-shadow-lg">
              Customization Features
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Feature 1 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center mb-4">
                  <div className="bg-white/20 rounded-full p-3 mr-4">
                    <Icon name="Palette" size={24} className="text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-white">Custom Themes</h3>
                </div>
                <p className="font-body text-white/90 text-sm">
                  Choose from a variety of color themes to match your personal style and professional brand.
                </p>
              </div>

              {/* Feature 2 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center mb-4">
                  <div className="bg-white/20 rounded-full p-3 mr-4">
                    <Icon name="Layout" size={24} className="text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-white">Planner Types</h3>
                </div>
                <p className="font-body text-white/90 text-sm">
                  Select from yearly, monthly, weekly, and daily planner formats tailored to your workflow.
                </p>
              </div>

              {/* Feature 3 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center mb-4">
                  <div className="bg-white/20 rounded-full p-3 mr-4">
                    <Icon name="Type" size={24} className="text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-white">Font Options</h3>
                </div>
                <p className="font-body text-white/90 text-sm">
                  Personalize your planner with professional fonts that enhance readability and aesthetics.
                </p>
              </div>

              {/* Feature 4 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center mb-4">
                  <div className="bg-white/20 rounded-full p-3 mr-4">
                    <Icon name="FileText" size={24} className="text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-white">Page Layouts</h3>
                </div>
                <p className="font-body text-white/90 text-sm">
                  Customize page layouts with schedules, tasks, journals, and habit trackers.
                </p>
              </div>

              {/* Feature 5 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center mb-4">
                  <div className="bg-white/20 rounded-full p-3 mr-4">
                    <Icon name="Download" size={24} className="text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-white">PDF Export</h3>
                </div>
                <p className="font-body text-white/90 text-sm">
                  Download high-quality PDF planners ready for printing or digital use.
                </p>
              </div>

              {/* Feature 6 - Premium */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center mb-4">
                  <div className="bg-white/20 rounded-full p-3 mr-4">
                    <Icon name="Crown" size={24} className="text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-white">Premium Branding</h3>
                </div>
                <p className="font-body text-white/90 text-sm">
                  Replace or hide the StillFlow logo for fully customized, professional branding.
                </p>
              </div>
            </div>
          </div>

          {/* Pricing Section */}
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-6 sm:p-8 mb-12 animate-fade-in">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-8 text-center drop-shadow-lg">
              Simple, Transparent Pricing
            </h2>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="font-heading text-white py-4 px-4 text-lg">Plan</th>
                    <th className="font-heading text-white py-4 px-4 text-lg">Price</th>
                    <th className="font-heading text-white py-4 px-4 text-lg">Features</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Free Plan */}
                  <tr className="border-b border-white/10">
                    <td className="py-4 px-4">
                      <div className="flex items-center">
                        <Icon name="Gift" size={20} className="text-white mr-2" />
                        <span className="font-heading text-white font-semibold">Free</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="font-heading text-2xl font-bold text-white">$0</span>
                    </td>
                    <td className="py-4 px-4">
                      <p className="font-body text-white/90 text-sm">
                        Sample PDF planners to evaluate features
                      </p>
                    </td>
                  </tr>

                  {/* 3 Month Plan */}
                  <tr className="border-b border-white/10">
                    <td className="py-4 px-4">
                      <div className="flex items-center">
                        <Icon name="Calendar" size={20} className="text-white mr-2" />
                        <span className="font-heading text-white font-semibold">3 Months</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="font-heading text-2xl font-bold text-white">$10</span>
                    </td>
                    <td className="py-4 px-4">
                      <ul className="font-body text-white/90 text-sm space-y-1">
                        <li>• 5 planners included</li>
                        <li>• $5 per extra planner</li>
                        <li>• 40 downloads per year</li>
                      </ul>
                    </td>
                  </tr>

                  {/* 1 Year Plan */}
                  <tr className="border-b border-white/10">
                    <td className="py-4 px-4">
                      <div className="flex items-center">
                        <Icon name="Star" size={20} className="text-white mr-2" />
                        <span className="font-heading text-white font-semibold">1 Year</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="font-heading text-2xl font-bold text-white">$30</span>
                    </td>
                    <td className="py-4 px-4">
                      <ul className="font-body text-white/90 text-sm space-y-1">
                        <li>• 20 planners included</li>
                        <li>• $3 per extra planner</li>
                        <li>• 200 downloads per year</li>
                      </ul>
                    </td>
                  </tr>

                  {/* Premium Option */}
                  <tr>
                    <td className="py-4 px-4">
                      <div className="flex items-center">
                        <Icon name="Crown" size={20} className="text-white mr-2" />
                        <span className="font-heading text-white font-semibold">Premium</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="font-heading text-lg font-semibold text-white">Custom</span>
                    </td>
                    <td className="py-4 px-4">
                      <p className="font-body text-white/90 text-sm">
                        Replace/hide logo for customized branding
                        <br />
                        <a href="mailto:support@stillflow.ink" className="text-white underline hover:text-white/80">
                          Contact support@stillflow.ink
                        </a> for specific needs/pricing
                      </p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* CTA Section */}
          <div className="text-center animate-fade-in">
            <button
              onClick={handleSignUp}
              className="inline-flex items-center space-x-3 py-4 px-8 bg-white hover:bg-white/90 text-primary font-heading text-lg font-bold rounded-xl transition-all duration-300 shadow-2xl hover:shadow-xl hover:scale-105 active:scale-95"
            >
              <Icon name="Sparkles" size={24} />
              <span>Sign Up Now</span>
              <Icon name="ArrowRight" size={24} />
            </button>
            <p className="font-body text-white/90 text-sm mt-4 drop-shadow">
              Start creating your perfect planner today
            </p>
          </div>
        </div>
      </div>
      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <div className="relative max-w-7xl w-full h-full flex items-center justify-center">
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 rounded-full p-3 transition-all duration-300 hover:scale-110 z-20"
              aria-label="Close lightbox"
            >
              <Icon name="X" size={24} className="text-white" />
            </button>

            {/* Previous Button */}
            <button
              onClick={(e) => {
                e?.stopPropagation();
                prevLightboxImage();
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 rounded-full p-4 transition-all duration-300 hover:scale-110 z-20"
              aria-label="Previous image"
            >
              <Icon name="ChevronLeft" size={32} className="text-white" />
            </button>

            {/* Image */}
            <div className="max-w-5xl max-h-[90vh]" onClick={(e) => e?.stopPropagation()}>
              <AppImage
                src={images?.[lightboxIndex]?.url}
                alt={images?.[lightboxIndex]?.caption || 'Planner showcase image'}
                className="w-full h-full object-contain rounded-lg"
              />
            </div>

            {/* Next Button */}
            <button
              onClick={(e) => {
                e?.stopPropagation();
                nextLightboxImage();
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 rounded-full p-4 transition-all duration-300 hover:scale-110 z-20"
              aria-label="Next image"
            >
              <Icon name="ChevronRight" size={32} className="text-white" />
            </button>

            {/* Image Counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-4 py-2">
              <span className="font-body text-white text-sm">
                {lightboxIndex + 1} / {images?.length}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShowcaseGallery;